import React, { useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField as MuiTextField, IconButton, Radio, Button, Paper, Typography,Box
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { useRecordContext, ReferenceField, TextField, RecordContextProvider, useDataProvider } from 'react-admin';

// Mapping functions for workflow fields
const getWorkflowCategoryLabel = (id) => {
  const categories = [
    { id: 1, name: "استبعاد وأنذار السائقين" },
    { id: 2, name: "استبعاد وأنذار المرافقين" }
  ];
  const category = categories.find(cat => cat.id === parseInt(id));
  return category ? category.name : id;
};

const getWorkflowSubcategoryLabel = (id) => {
  const subcategories = [
    { id: 1, name: "مسودة طلب استبعاد أو إنذار" },
    { id: 2, name: "اعتماد مشرف المنطقة لطلب الاستبعاد أو الإنذار" },
    { id: 3, name: "اعتماد اخصائي الامن والسلامة لطلب الاستبعاد أو الإنذار" },
    { id: 4, name: "اعتماد مدير المنطقة لطلب الاستبعاد أو الإنذار" },
    { id: 5, name: "طلب الاستبعاد أو الإنذار مقفل" },
    { id: 6, name: "طلب الاستبعاد أو الإنذار ملغى" }
  ];
  const subcategory = subcategories.find(sub => sub.id === parseInt(id));
  return subcategory ? subcategory.name : id;
};

  const BlacklistedDriverField = () => {
    const record = useRecordContext(); // Get the current form record
    const dataProvider = useDataProvider();
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(false);

    // Watch form fields for real-time updates (for create mode)
    const { control } = useFormContext();
    const formDriverNid = useWatch({ control, name: 'driver_nid', defaultValue: null });
    const formWorkflowCategory = useWatch({ control, name: 'workflow_category', defaultValue: null });
    const formIsSearchDone = useWatch({ control, name: 'is_search_done', defaultValue: false });
    
    // Use form values if available (create mode), otherwise use record values (edit/show mode)
    const driverNid = formDriverNid || record?.driver_nid;
    const workflowCategory = formWorkflowCategory || record?.workflow_category;

    // Check if we're in create mode (no record.id)
    const isCreateMode = !record?.id;
    // In create mode, only show if is_search_done is true
    // In edit/show mode, always show if we have driver_nid and workflow_category
    const shouldShow = isCreateMode ? formIsSearchDone : (driverNid && workflowCategory);

  const columnConfig = [
    { field: 'id', label: 'رقم التذكرة', ttype: 'readonly' },
    { field: 'workflow_category', label: 'نموذج الطلب', ttype: 'readonly' },
    { field: 'workflow_subcategory', label: 'الاجراء المطلوب', ttype: 'readonly' },
    { field: 'driver_nid', label: 'رقم هوية السائق/المرافق', ttype: 'readonly' },
    { field: 'education_area_id', label: 'المنطقة', ttype: 'reference', resource: 'domain_sectors', source: 'name' },
    { field: 'education_zone_id', label: 'إدارة التعليم', ttype: 'reference', resource: 'domain_administrations', source: 'name' },
    { field: 'academic_year', label: 'العام الدراسي بالهجري', ttype: 'readonly' },
    { field: 'period_id', label: 'الفترة', ttype: 'reference', resource: 'ods_operational_periods', source: 'period_id' },
    { field: 'vendor_id', label: 'المتعهد', ttype: 'reference', resource: 'vendors', source: 'vendor_name' },
    { field: 'accident_type', label: 'الإجراء المتخذ', ttype: 'readonly' },
    { field: 'incident_cause', label: 'نوع المخالفة', ttype: 'readonly' },
    { field: 'previous_warning_count', label: 'عدد الانذارات', ttype: 'readonly' },
    { field: 'previous_warning_text', label: 'نوع الانذار', ttype: 'readonly' },
  ];



  useEffect(() => {
    const fetchBlacklistData = async () => {
      if (!dataProvider) return;
      
      // Don't fetch if shouldShow is false (not in the right mode/state)
      if (!shouldShow) {
        setPlans([]);
        setLoading(false);
        return;
      }

      // Don't fetch if we don't have the required data
      if (!driverNid || !workflowCategory) {
        setPlans([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        // Always use workflow_subcategory 5,6 for Field component (edit/show modes)
        // Get blacklist data based on category and NID
        const operationPlansFilter = {
          "workflow_subcategory@_in": [5, 6],
          "workflow_category@_eq": workflowCategory,
          "driver_nid@_eq": driverNid,
        };

        const operationPlansResponse = await dataProvider.getList("blacklist_drivers_log", {
          filter: operationPlansFilter,
          pagination: { page: 1, perPage: 10 },
        });

        setPlans(operationPlansResponse.data || []);
      } catch (error) {
        console.error("Error fetching blacklist data:", error);
        setPlans([]);
      } finally {
        setLoading(false);
      }
    };

    fetchBlacklistData();
  }, [shouldShow, driverNid, workflowCategory, dataProvider]);






  return (
    <>
      {/* Only check is_search_done in create mode; in edit/show modes, show if we have data */}
      {!shouldShow ? null : loading ? (
        <div style={{ textAlign: 'center', padding: '20px', fontSize: '16px', color: 'gray' }}>
          جاري التحميل...
        </div>
      ) : plans && plans.length > 0 ? (
        <Box sx={{ width: '95vw', maxWidth:'95vw', overflow: 'auto', maxHeight: '400px' }}>
          <TableContainer
            component={Paper}
            sx={{
              width: 'max-content', // Forces table to expand horizontally within the scrollable container
              minWidth: 1500,
            }}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {columnConfig.map((col) => (
                    <TableCell
                      key={col.field}
                      sx={{
                        height: '50px',
                        textAlign: 'center',
                        whiteSpace: 'nowrap',
                        minWidth: '150px',
                      }}
                    >
                      {col.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {plans.map((plan) => (
                  <TableRow key={plan.id}>
                    {columnConfig.map((col) => {
                      let displayValue = plan[col.field] ? plan[col.field] : "--";
                      
                      // Apply label mapping for specific fields
                      if (col.field === 'workflow_category' && plan[col.field]) {
                        displayValue = getWorkflowCategoryLabel(plan[col.field]);
                      } else if (col.field === 'workflow_subcategory' && plan[col.field]) {
                        displayValue = getWorkflowSubcategoryLabel(plan[col.field]);
                      }
                      
                      return (
                        <TableCell
                          key={col.field}
                          sx={{
                            height: '50px',
                            textAlign: 'center',
                            whiteSpace: 'nowrap',
                            minWidth: '150px',
                          }}
                        >
                          {col.ttype === 'reference' && plan[col.field] ? (
                            <RecordContextProvider value={plan}>
                              <ReferenceField 
                                source={col.field}
                                reference={col.resource}
                                link={false}
                              >
                                <TextField source={col.source} />
                              </ReferenceField>
                            </RecordContextProvider>
                          ) : (
                            displayValue
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ) : shouldShow ? (
        <div style={{ textAlign: 'center', padding: '20px', fontSize: '16px', color: 'gray' }}>
         لا يوجد طلب استبعاد محدد فى النظام بعد
        </div>
      ) : null}
    </>
  );
  
};

export default BlacklistedDriverField;
